import { createContext, useContext, useCallback, useMemo, useEffect } from 'react'
import { useAccount, useConnect, useDisconnect, useConfig } from 'wagmi'
import { getConnectors } from '@wagmi/core'
import {
  findConnectorForWallet,
  resolveWalletConnectModalConnector,
} from '../utils/walletConnectorMap'
import { defaultChainId } from '../utils/evm/config.js'
import { tagAddress } from '../config/logrocket'

export const DEFAULT_TRADE_PATH = '/trade/LIT'

const WalletAuthContext = createContext()

export function WalletAuthProvider({ children }) {
  const config = useConfig()
  const { address, isConnected } = useAccount()
  const { connectAsync } = useConnect()
  const { disconnectAsync } = useDisconnect()
  const connectors = useMemo(() => getConnectors(config), [config])

  useEffect(() => {
    if (address) tagAddress(address)
  }, [address])

  const markSignedIn = useCallback(() => {
    // Connection state is driven by wagmi; kept for API compatibility.
  }, [])

  const connect = useCallback(async (walletId) => {
    if (!walletId) {
      throw new Error('Wallet id is required')
    }

    const connector = findConnectorForWallet(connectors, walletId)
    if (!connector) {
      throw new Error(`No connector found for wallet: ${walletId}`)
    }

    let targetConnector = connector
    if (
      connector.type === 'walletConnect'
      && !connector.rkDetails?.showQrModal
    ) {
      const modalConnector = resolveWalletConnectModalConnector(connectors)
      if (modalConnector) {
        targetConnector = modalConnector
      }
    }

    const result = await connectAsync({
      connector: targetConnector,
      chainId: defaultChainId,
    })
    return result.accounts[0]
  }, [connectAsync, connectors])

  const logout = useCallback(async () => {
    await disconnectAsync()
    sessionStorage.removeItem('lighter_user_address')
    sessionStorage.removeItem('lighter_jwt_token')
  }, [disconnectAsync])

  const value = {
    isConnected,
    isAuthenticated: isConnected,
    userAddress: address ?? null,
    connect,
    markSignedIn,
    logout,
  }

  return (
    <WalletAuthContext.Provider value={value}>
      {children}
    </WalletAuthContext.Provider>
  )
}

export function useWalletAuth() {
  const context = useContext(WalletAuthContext)
  if (!context) {
    throw new Error('useWalletAuth must be used within WalletAuthProvider')
  }
  return context
}
